import { useState, useEffect, useRef } from 'react';
import type { ScheduleItem } from '@modelcast/types';
import { Modal } from '../../components/Modal';

const STORAGE_KEY_POSE_DURATION = 'lifedrawing-last-pose-duration';
const STORAGE_KEY_BREAK_DURATION = 'lifedrawing-last-break-duration';
const STORAGE_KEY_BETWEEN_POSES_DURATION = 'lifedrawing-between-poses-duration';
const DEFAULT_BETWEEN_POSES_DURATION = 30;
const BETWEEN_POSES_NAME = 'Between poses';

function isTransitionBreak(item: ScheduleItem) {
  return item.type === 'break' && item.isTransition === true;
}

function getTransitionDuration(schedule: ScheduleItem[], fallback: number) {
  return schedule.find(isTransitionBreak)?.duration ?? fallback;
}

function createTransitionBreak(duration: number): ScheduleItem {
  return {
    type: 'break',
    name: BETWEEN_POSES_NAME,
    duration,
    isTransition: true,
  };
}

function shouldInsertTransitionBetween(
  currentItem: ScheduleItem | undefined,
  nextItem: ScheduleItem | undefined,
  duration: number
) {
  return duration > 0 && currentItem?.type === 'pose' && nextItem?.type === 'pose';
}

function withMissingTransitionBreaks(schedule: ScheduleItem[], duration: number): ScheduleItem[] {
  return schedule.flatMap((item, index) => {
    if (!shouldInsertTransitionBetween(item, schedule[index + 1], duration)) {
      return [item];
    }

    return [item, createTransitionBreak(duration)];
  });
}

function formatDuration(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}

interface NewPoseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: ScheduleItem[]) => void;
  /** Existing schedule to edit (if any) */
  initialSchedule?: ScheduleItem[];
}

export function NewPoseModal({ isOpen, onClose, onSave, initialSchedule = [] }: NewPoseModalProps) {
  const wasOpenRef = useRef(false);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [lastPoseDuration, setLastPoseDuration] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_POSE_DURATION);
    return stored ? parseInt(stored) : 300; // 5 minutes default
  });
  const [lastBreakDuration, setLastBreakDuration] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_BREAK_DURATION);
    return stored ? parseInt(stored) : 60; // 1 minute default
  });
  const [betweenPoseDuration, setBetweenPoseDuration] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_BETWEEN_POSES_DURATION);
    if (stored === null) return DEFAULT_BETWEEN_POSES_DURATION;

    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? DEFAULT_BETWEEN_POSES_DURATION : Math.max(0, parsed);
  });

  // Initialize with existing schedule or one new pose when opened
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!justOpened) {
      return;
    }

    const transitionDuration = getTransitionDuration(initialSchedule, betweenPoseDuration);
    setBetweenPoseDuration(transitionDuration);

    if (initialSchedule.length > 0) {
      setSchedule(initialSchedule.map(item => ({ ...item })));
    } else {
      setSchedule([{ type: 'pose', name: 'Pose 1', duration: lastPoseDuration }]);
    }
  }, [isOpen, initialSchedule, betweenPoseDuration, lastPoseDuration]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setSchedule([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAddPose = () => {
    const poseCount = schedule.filter(s => s.type === 'pose').length;
    const newItem: ScheduleItem = {
      type: 'pose',
      name: `Pose ${poseCount + 1}`,
      duration: lastPoseDuration,
    };
    const transition = shouldInsertTransitionBetween(schedule[schedule.length - 1], newItem, betweenPoseDuration)
      ? [createTransitionBreak(betweenPoseDuration)]
      : [];

    setSchedule([...schedule, ...transition, newItem]);
  };

  const handleAddBreak = () => {
    const newItem: ScheduleItem = {
      type: 'break',
      name: 'Break',
      duration: lastBreakDuration,
    };
    setSchedule([...schedule, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    if (schedule.length > 1) {
      setSchedule(schedule.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, field: 'name' | 'duration', value: string | number) => {
    const updated = [...schedule];
    if (field === 'name') {
      updated[index] = { ...updated[index], name: value as string };
    } else if (field === 'duration') {
      const duration = Math.max(1, Number(value));
      updated[index] = { ...updated[index], duration };
      // Remember last used duration for this type
      if (updated[index].type === 'pose') {
        setLastPoseDuration(duration);
        localStorage.setItem(STORAGE_KEY_POSE_DURATION, String(duration));
      } else if (!isTransitionBreak(updated[index])) {
        setLastBreakDuration(duration);
        localStorage.setItem(STORAGE_KEY_BREAK_DURATION, String(duration));
      }
    }
    setSchedule(updated);
  };

  const handleBetweenPoseDurationChange = (value: string) => {
    const duration = Math.max(0, parseInt(value, 10) || 0);
    setBetweenPoseDuration(duration);
    localStorage.setItem(STORAGE_KEY_BETWEEN_POSES_DURATION, String(duration));
    setSchedule(currentSchedule => {
      if (duration === 0) {
        return currentSchedule.filter(item => !isTransitionBreak(item));
      }

      const updatedSchedule = currentSchedule.map(item =>
        isTransitionBreak(item) ? { ...item, duration } : item
      );
      return withMissingTransitionBreaks(updatedSchedule, duration);
    });
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === schedule.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...schedule];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSchedule(updated);
  };

  const handleSave = () => {
    if (schedule.length === 0) return;
    onSave(schedule);
    onClose();
  };

  const totalTime = schedule.reduce((acc, item) => acc + item.duration, 0);
  const poseCount = schedule.filter(s => s.type === 'pose').length;
  const breakCount = schedule.filter(s => s.type === 'break').length;

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/40 backdrop-blur-sm"
      panelClassName="bg-panel border border-line rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Build pose schedule"
    >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <h2 className="font-display text-lg font-medium text-text">Build Pose Schedule</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-text-dim hover:text-text hover:bg-bg-secondary transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-line bg-panel-secondary px-3 py-3">
            <span className="text-[12px] font-medium text-text">Default between poses</span>
            <div className="flex items-center gap-1 text-sm">
              <input
                type="number"
                min="0"
                max="999"
                value={betweenPoseDuration}
                onChange={(e) => handleBetweenPoseDurationChange(e.target.value)}
                aria-label="Seconds between poses"
                className="w-16 bg-bg-secondary text-text text-center border border-line rounded-lg px-1 py-1 focus:border-accent/40 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-text-faint text-[10px]">s</span>
            </div>
          </div>

          {/* Schedule list */}
          <div className="space-y-2 mb-6">
            {schedule.map((item, index) => (
              <ScheduleItemRow
                key={index}
                item={item}
                index={index}
                total={schedule.length}
                onChange={handleItemChange}
                onRemove={handleRemoveItem}
                onMove={handleMoveItem}
              />
            ))}
          </div>

          {/* Add buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleAddPose}
              className="flex-1 py-2.5 rounded-xl border border-dashed border-accent/40 text-accent text-sm hover:border-accent hover:bg-accent-soft transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-lg">+</span> Add Pose
            </button>
            <button
              onClick={handleAddBreak}
              className="flex-1 py-2.5 rounded-xl border border-dashed border-text-dim/40 text-text-dim text-sm hover:border-text-dim hover:bg-panel-secondary transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-lg">+</span> Add Break
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex items-center justify-between">
          <div className="text-sm text-text-dim">
            Total: <span className="text-text font-medium">{formatDuration(totalTime)}</span>
            <span className="mx-2">·</span>
            {poseCount} pose{poseCount !== 1 ? 's' : ''}
            {breakCount > 0 && `, ${breakCount} break${breakCount !== 1 ? 's' : ''}`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={schedule.length === 0}
              className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
    </Modal>
  );
}

interface ScheduleItemRowProps {
  item: ScheduleItem;
  index: number;
  total: number;
  onChange: (index: number, field: 'name' | 'duration', value: string | number) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
}

function ScheduleItemRow({ item, index, total, onChange, onRemove, onMove }: ScheduleItemRowProps) {
  const isPose = item.type === 'pose';
  const isTransition = isTransitionBreak(item);
  const bgClass = isPose ? 'bg-accent-soft' : isTransition ? 'bg-panel-secondary/70' : 'bg-panel-secondary';
  const borderClass = isPose ? 'border-accent/30' : isTransition ? 'border-line border-dashed' : 'border-line';
  const labelClass = isPose ? 'text-accent' : isTransition ? 'text-text-faint' : 'text-text-dim';
  const dotClass = isPose ? 'bg-accent' : isTransition ? 'bg-line' : 'bg-text-dim';

  return (
    <div className={`flex w-full items-center gap-3 rounded-xl border ${isTransition ? 'p-2.5' : 'p-3'} ${bgClass} ${borderClass}`}>
      {/* Reorder buttons */}
      <div className="flex flex-col gap-0.5">
        <button
          onClick={() => onMove(index, 'up')}
          disabled={index === 0}
          className="w-5 h-5 flex items-center justify-center rounded text-text-faint hover:text-text hover:bg-panel-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 6l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          onClick={() => onMove(index, 'down')}
          disabled={index === total - 1}
          className="w-5 h-5 flex items-center justify-center rounded text-text-faint hover:text-text hover:bg-panel-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 4l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Type indicator */}
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        <span className={`text-[10px] font-semibold uppercase ${labelClass}`}>
          {isTransition ? 'between' : item.type}
        </span>
      </div>

      {/* Name input */}
      <input
        type="text"
        value={item.name}
        onChange={(e) => onChange(index, 'name', e.target.value)}
        className={`flex-1 bg-transparent border-b border-transparent focus:border-accent/40 outline-none min-w-0 ${
          isTransition
            ? 'font-mono text-[10px] uppercase text-text-dim'
            : 'text-text text-sm'
        }`}
        placeholder={isPose ? 'Pose name' : isTransition ? BETWEEN_POSES_NAME : 'Break'}
      />

      {/* Duration input */}
      <DurationInput
        value={item.duration}
        onChange={(duration) => onChange(index, 'duration', duration)}
      />

      {/* Remove button */}
      {total > 1 && (
        <button
          onClick={() => onRemove(index)}
          className="w-7 h-7 flex items-center justify-center rounded text-text-faint hover:text-warn hover:bg-warn/10 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 7h8" />
          </svg>
        </button>
      )}
    </div>
  );
}

interface DurationInputProps {
  value: number; // seconds
  onChange: (seconds: number) => void;
}

function DurationInput({ value, onChange }: DurationInputProps) {
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const mins = Math.max(0, parseInt(e.target.value) || 0);
    onChange(mins * 60 + seconds);
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let secs = Math.max(0, parseInt(e.target.value) || 0);
    // Allow seconds up to 59, or any value if no minutes
    if (minutes > 0) {
      secs = Math.min(59, secs);
    }
    onChange(minutes * 60 + secs);
  };

  return (
    <div className="flex items-center gap-1 text-sm">
      <input
        type="number"
        min="0"
        max="999"
        value={minutes}
        onChange={handleMinutesChange}
        className="w-12 bg-bg-secondary text-text text-center border border-line rounded-lg px-1 py-1 focus:border-accent/40 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-text-faint text-[10px]">m</span>
      <input
        type="number"
        min="0"
        max={minutes > 0 ? 59 : 999}
        value={seconds}
        onChange={handleSecondsChange}
        className="w-12 bg-bg-secondary text-text text-center border border-line rounded-lg px-1 py-1 focus:border-accent/40 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-text-faint text-[10px]">s</span>
    </div>
  );
}
